'use client';

import type { IconButtonProps } from '@chakra-ui/react';
import { ClientOnly, IconButton, Skeleton } from '@chakra-ui/react';
import type { ThemeProviderProps } from 'next-themes';
import { ThemeProvider, useTheme } from 'next-themes';
import * as React from 'react';
import { LuMoon, LuSun } from 'react-icons/lu';
import { chromeControlStyles } from './sidebar';

export interface ColorModeProviderProps extends ThemeProviderProps {}

export function ColorModeProvider(props: ColorModeProviderProps) {
    return (
        <ThemeProvider attribute='class' disableTransitionOnChange {...props} />
    );
}

export type ColorMode = 'light' | 'dark';

export interface UseColorModeReturn {
    colorMode: ColorMode;
    setColorMode: (colorMode: ColorMode) => void;
    toggleColorMode: () => void;
}

export function useColorMode(): UseColorModeReturn {
    const { resolvedTheme, setTheme, forcedTheme } = useTheme();
    const colorMode = forcedTheme || resolvedTheme;
    const toggleColorMode = () => {
        setTheme(colorMode === 'dark' ? 'light' : 'dark');
    };
    return {
        colorMode: colorMode as ColorMode,
        setColorMode: setTheme,
        toggleColorMode,
    };
}

export function ColorModeIcon() {
    const { colorMode } = useColorMode();
    return colorMode === 'dark' ? <LuMoon /> : <LuSun />;
}

interface ColorModeButtonProps extends Omit<IconButtonProps, 'aria-label'> {}

export const ColorModeButton = React.forwardRef<
    HTMLButtonElement,
    ColorModeButtonProps
>(function ColorModeButton({ size = 'sm', ...props }, ref) {
    const { toggleColorMode } = useColorMode();
    // Under a forced theme (a host pinning one mode, e.g. a test harness)
    // the toggle would be a silent no-op — hide it instead of asking every
    // host to gate it.
    const { forcedTheme } = useTheme();
    // Glyph tracks the control: 16px in compact xs hosts (sidebar-footer
    // rows), 20px otherwise.
    const iconSize = size === 'xs' ? '4' : '5';
    if (forcedTheme) return null;
    return (
        <ClientOnly fallback={<Skeleton boxSize='8' />}>
            <IconButton
                onClick={toggleColorMode}
                // The shared chrome-control look (same ghost treatment as
                // the sidebar toggle). Overridable — props spread after.
                variant='ghost'
                {...chromeControlStyles}
                aria-label='Toggle color mode'
                size={size}
                ref={ref}
                {...props}
                css={{
                    _icon: {
                        width: iconSize,
                        height: iconSize,
                    },
                }}
            >
                <ColorModeIcon />
            </IconButton>
        </ClientOnly>
    );
});
